import { useEffect, useMemo, useState } from "react";
import { profile } from "../components/funtionsBD/Mainservice";

export const DEFAULT_PLAYER_PHYSIOLOGY = {
  age: 30,
  gender: "male",
  weightKg: 70,
};

export function computeAgeFromDob(dob) {
  if (!dob) return null;
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age -= 1;
  return age > 0 ? age : null;
}

export function normalizeGender(raw) {
  return String(raw || "male").toLowerCase() === "female" ? "female" : "male";
}

export function physiologyFromFields({
  age,
  gender,
  dateOfBirth,
  dob,
  weightKg,
  weight,
} = {}) {
  const resolvedGender = normalizeGender(gender);
  let resolvedAge = Number(age);
  if (!Number.isFinite(resolvedAge) || resolvedAge <= 0) {
    resolvedAge = computeAgeFromDob(dateOfBirth ?? dob);
  }
  if (!Number.isFinite(resolvedAge) || resolvedAge <= 0) {
    resolvedAge = DEFAULT_PLAYER_PHYSIOLOGY.age;
  }
  let resolvedWeight = Number(weightKg ?? weight);
  if (!Number.isFinite(resolvedWeight) || resolvedWeight <= 0) {
    resolvedWeight = DEFAULT_PLAYER_PHYSIOLOGY.weightKg;
  }
  return {
    age: resolvedAge,
    gender: resolvedGender,
    weightKg: resolvedWeight,
  };
}

/** getProfile API user row. */
export function physiologyFromProfileUser(user) {
  if (!user || typeof user !== "object" || Array.isArray(user)) {
    return { ...DEFAULT_PLAYER_PHYSIOLOGY };
  }
  return physiologyFromFields({
    age: user.age,
    gender: user.user_sex ?? user.gender ?? user.sex,
    dateOfBirth:
      user.user_date_birth ?? user.dateOfBirth ?? user.birth_date,
    weightKg: user.user_weight ?? user.weight,
  });
}

/** HR picker row and/or group roster player (playerAndCoachgroupList). */
export function physiologyFromPickerSources({ hrRow, groupPlayer } = {}) {
  const dateOfBirth =
    hrRow?.user_date_birth ??
    hrRow?.birth_date ??
    hrRow?.date_birth ??
    hrRow?.dateOfBirth ??
    groupPlayer?.user_date_birth ??
    groupPlayer?.birth_date ??
    groupPlayer?.date_birth ??
    groupPlayer?.dateOfBirth ??
    null;
  const age =
    hrRow?.age ??
    hrRow?.player_age ??
    groupPlayer?.age ??
    groupPlayer?.player_age ??
    null;
  return {
    physiology: physiologyFromFields({
      age,
      gender:
        hrRow?.user_sex ??
        hrRow?.gender ??
        hrRow?.sex ??
        groupPlayer?.user_sex ??
        groupPlayer?.gender ??
        groupPlayer?.sex,
      dateOfBirth,
      weightKg:
        hrRow?.user_weight ??
        hrRow?.weight ??
        groupPlayer?.user_weight ??
        groupPlayer?.weight,
    }),
    hasDob: !!(dateOfBirth || (Number(age) > 0)),
  };
}

export function buildPlayerOptionPhysiology(fromHrRow, groupPlayer) {
  return physiologyFromPickerSources({ hrRow: fromHrRow, groupPlayer });
}

export function physiologyFromSubjectProfile(subjectProfile) {
  if (!subjectProfile || typeof subjectProfile !== "object") {
    return { ...DEFAULT_PLAYER_PHYSIOLOGY };
  }
  return physiologyFromFields({
    age: subjectProfile.age,
    gender: subjectProfile.gender,
    dateOfBirth: subjectProfile.dateOfBirth,
    weightKg: subjectProfile.weightKg ?? subjectProfile.weight,
  });
}

/** Logged-in player: localStorage user + profiling form. */
export function getPlayerPhysiologyFromStorage() {
  let user = {};
  try {
    user = JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    /* ignore */
  }
  let profiling = {};
  try {
    const raw =
      localStorage.getItem("profilingData") ||
      localStorage.getItem("playerFormData");
    if (raw) profiling = JSON.parse(raw);
  } catch {
    /* ignore */
  }
  return physiologyFromFields({
    age: user.age ?? profiling.age,
    gender:
      profiling.gender ??
      user.user_sex ??
      user.gender ??
      user.sex,
    dateOfBirth:
      profiling.dateOfBirth ??
      user.user_date_birth ??
      user.dateOfBirth ??
      user.birth_date,
    weightKg:
      profiling.weight ?? user.user_weight ?? user.weight,
  });
}

/**
 * Resolve age/gender/weight for HR zone math.
 * - Own session: localStorage profile
 * - Coach/staff viewing another player: profile API (view_user_id / view_player_id)
 */
export function useSubjectPhysiology({
  viewUserId = null,
  viewPlayerId = null,
  subjectProfile = null,
  useStorageFallback = false,
  inlinePhysiology = null,
  inlineHasDob = false,
} = {}) {
  const explicitProfile = useMemo(() => {
    if (subjectProfile && typeof subjectProfile === "object") {
      return physiologyFromSubjectProfile(subjectProfile);
    }
    return null;
  }, [subjectProfile]);

  const inlineResolved = useMemo(() => {
    if (!inlinePhysiology || typeof inlinePhysiology !== "object") return null;
    if (inlineHasDob) return inlinePhysiology;
    return null;
  }, [inlinePhysiology, inlineHasDob]);

  const targetUserId =
    viewUserId != null && String(viewUserId).trim() !== ""
      ? String(viewUserId).trim()
      : null;
  const targetPlayerId =
    viewPlayerId != null && String(viewPlayerId).trim() !== ""
      ? String(viewPlayerId).trim()
      : null;
  const storagePhysiology = useMemo(
    () => (useStorageFallback ? getPlayerPhysiologyFromStorage() : null),
    // Re-read when identity targets change (login / subject switch).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [useStorageFallback, targetUserId, targetPlayerId]
  );

  const needsFetch =
    !!(targetUserId || targetPlayerId) && !explicitProfile && !inlineResolved;

  const [fetched, setFetched] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!needsFetch) {
      setFetched(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setFetched(null);
    setLoading(true);
    (async () => {
      try {
        const opts = {};
        if (targetUserId) opts.view_user_id = targetUserId;
        else if (targetPlayerId) opts.view_player_id = targetPlayerId;
        const res = await profile(opts);
        const data = res?.data?.data;
        if (!cancelled) {
          setFetched(
            data && typeof data === "object" && !Array.isArray(data)
              ? physiologyFromProfileUser(data)
              : null
          );
        }
      } catch {
        if (!cancelled) setFetched(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [needsFetch, targetUserId, targetPlayerId]);

  const physiology = useMemo(() => {
    if (explicitProfile) return explicitProfile;
    if (inlineResolved) return inlineResolved;
    if (needsFetch && fetched) return fetched;
    if (useStorageFallback && storagePhysiology) return storagePhysiology;
    if (needsFetch) return fetched ?? { ...DEFAULT_PLAYER_PHYSIOLOGY };
    return { ...DEFAULT_PLAYER_PHYSIOLOGY };
  }, [
    explicitProfile,
    inlineResolved,
    needsFetch,
    fetched,
    useStorageFallback,
    storagePhysiology,
  ]);

  return { physiology, loading: needsFetch && loading };
}
